/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Grid,
    Typography,
} from '@mui/material';
import { JourneyType } from 'app/constants/Booking/BookingConstants';
import { StepComponentProps } from 'components/RouteWizard/RouteWizard';
import { useWizard } from 'components/RouteWizard/Wizard';
import useClient from 'hooks/useClient';
import useClients from 'hooks/useClients';
import useQuote from 'hooks/useQuote';
import { DateTime } from 'luxon';
import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import CheapestCards from '../GroupedSuppliers/parts/cheapest-cards';
import SupplierRows from '../GroupedSuppliers/parts/supplier-rows';
import { useDevLegacyQuoteUI } from '../GroupedSuppliers/useDevLegacyQuoteUI';

const GroupedSupplierOptions = ({ onSuccess }: StepComponentProps) => {
  const { data } = useWizard()
  const history = useHistory()
  const [legacyQuoteUI, setLegacyQuoteUI] = useDevLegacyQuoteUI()
  const {
    prices,
    reference,
    inboundReference,
    summary,
    createTypeOrderedQuote,
    isLoading,
    error,
    errorDetails,
  } = useQuote()
  const { clients } = useClients()
  const { client: clientDetails } = useClient(data.account?.clientId)
  const automaticAllocation = !!clientDetails?.settings?.automaticAllocation
  const quickQuote = useSelector((state: any) => state.bookings.quickQuote)
  const accountNumber = clients?.find(
    (c: any) => c.id === data.account?.clientId,
  )?.accountNumber
  const firstValidationError = errorDetails?.validationErrors?.[0]
  const quoteErrorStatus = errorDetails?.status
  const quoteErrorCode = errorDetails?.code
  const isServerQuoteError =
    (typeof quoteErrorStatus === 'number' &&
      quoteErrorStatus >= 500 &&
      quoteErrorStatus < 600) ||
    (typeof quoteErrorStatus !== 'number' &&
      typeof quoteErrorCode === 'string' &&
      quoteErrorCode.startsWith('SYS'))

  const handleCreateQuote = () => {
    if (!accountNumber) {
      return
    }

    const isImmediateJourney =
      data.plan?.journeyTypeName === JourneyType.ONE_WAY &&
      data.plan?.isImmediateJourney

    const getPassengers = (passengers: any) => {
      if (
        passengers === undefined ||
        passengers === null ||
        passengers.length === 0
      ) {
        return []
      }
      return passengers
        .filter((p: any) => !(p && Object.keys(p).length === 0))
        .map((p: any) => ({
          contactNumber: p.contactNumber,
          email: p.email,
          isLeadPassenger: p.isLeadPassenger,
          name: p.name,
          userId: p.userId,
        }))
    }

    const quoteData = {
      clientAccountNumber: accountNumber,
      onBehalfOf: data.account?.onBehalfOf?.id
        ? data.account?.onBehalfOf.id
        : '',
      numberOfPassengers: data.account?.numberOfPassengers,
      passengers: [
        ...getPassengers(data?.account?.passengers),
        data.account?.mainPassenger,
      ],

      bookingReferences: data.account?.bookingReferenceValues
        ? data.account?.bookingReferenceValues.filter((ref: any) => ref?.value)
        : [],
      journeyTypeName: data.plan?.journeyTypeName,
      isImmediateJourney,
      waitBeforeReturnTime:
        data.plan?.journeyTypeName === JourneyType.WAIT_AND_RETURN
          ? (parseInt(data.plan?.waitHours, 10) || 0) * 60 +
            (parseInt(data.plan?.waitMinutes, 10) || 0)
          : null,
      directedJourneyTime:
        data.plan?.journeyTypeName === JourneyType.AS_DIRECTED
          ? parseInt(data.plan?.directedJourneyTime, 10) || 0
          : null,
      outbound: {
        ...data.plan?.outbound,
        additionalStops: data.plan?.additionalStops,
        when: data.plan?.outbound?.when
          ? DateTime.fromISO(data.plan?.outbound.when)
              .setZone(data.plan?.outbound.timeZone, {
                keepLocalTime: true,
              })
              .toString()
          : '',
      },
      inbound: {
        ...data.plan?.inbound,
        when: data.plan?.inbound?.when
          ? DateTime.fromISO(data.plan?.inbound?.when)
              .setZone(data.plan?.outbound.timeZone, {
                keepLocalTime: true,
              })
              .toString()
          : '',
        timeZone: data.plan?.inbound ? data.plan?.outbound.timeZone : null,
      },
    }

    if (quoteData.bookingReferences?.length <= 0) {
      delete quoteData.bookingReferences
    }

    if (isImmediateJourney) {
      delete quoteData.outbound.when
    }

    if (quoteData.outbound?.additionalStops?.length <= 0) {
      delete quoteData.outbound?.additionalStops
    }

    if (data.plan?.outbound?.services == 'MeetAndGreet') {
      quoteData.outbound.services = [
        { serviceTypeName: 'MeetAndGreet', quantity: 1 },
      ]
    } else {
      delete quoteData.outbound.services
    }

    if (data.plan?.inbound?.services == 'MeetAndGreet') {
      quoteData.inbound.services = [
        { serviceTypeName: 'MeetAndGreet', quantity: 1 },
      ]
    } else {
      delete quoteData.inbound.services
    }

    if (quoteData.journeyTypeName !== JourneyType.RETURN) {
      delete quoteData.inbound
    }

    createTypeOrderedQuote(quoteData)
  }

  React.useEffect(() => {
    handleCreateQuote()
  }, [])

  if (isLoading) {
    return (
      <>
        <Box
          component="div"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
          }}
        >
          <Typography align="center" paragraph>
            Getting your quotes, please wait.
          </Typography>
          <CircularProgress size={12} />
        </Box>
      </>
    )
  }

  if (!isLoading && error) {
    return (
      <>
        <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
          <Grid item xs={7}>
            <Box
              component="div"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              <Typography align="center" paragraph>
                Sorry there was an error getting your quote.
              </Typography>
              <Alert severity="error">
                {!!errorDetails?.message && (
                  <Typography
                    align="center"
                    paragraph
                    sx={{ maxWidth: '520px', textWrap: 'pretty' }}
                  >
                    {errorDetails.message}
                  </Typography>
                )}
              </Alert>

              <Box
                component="div"
                sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}
              >
                {!isServerQuoteError && (
                  <Button onClick={() => history.goBack()}>Go Back</Button>
                )}
                {isServerQuoteError && (
                  <Button onClick={handleCreateQuote}>Try Again</Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </>
    )
  }

  return (
    <>
      {legacyQuoteUI || automaticAllocation ? (
        <CheapestCards
          quoteData={{ prices, reference, inboundReference, isLoading, error }}
          onSuccess={onSuccess}
        />
      ) : (
        <SupplierRows
          quoteData={{ prices, reference, inboundReference, isLoading, error }}
          onSuccess={onSuccess}
        />
      )}
    </>
  )
}

export default GroupedSupplierOptions
